/**
 * Price Calculator Module
 * Handles complex pricing logic, tax calculations, and price rule matching
 * Extracted from csvImportApiClient.js for better maintainability
 */

// import mockDataService from '../mockDataService'; // Available for future use

// Debug logging
const DEBUG_API = process.env.REACT_APP_DEBUG_API === 'true';

class PriceCalculator {
    constructor(baseApiClient) {
        this.baseClient = baseApiClient;
        this.api = baseApiClient.api; // Keep for backward compatibility
    }

    /**
     * Parse price from various formats
     * @param {string|number} priceValue - Price value to parse
     * @returns {number} Parsed price or 0
     */
    parsePrice(priceValue) {
        if (typeof priceValue === 'number') {
            return priceValue;
        }

        if (typeof priceValue === 'string') {
            // Handle German decimal format (123,45) and English format (123.45)
            const normalizedPrice = priceValue
                .replace(/[^\d,.-]/g, '') // Remove non-numeric characters except comma, dot, dash
                .replace(/,/g, '.'); // Replace comma with dot for consistent parsing
            
            const parsed = parseFloat(normalizedPrice);
            return isNaN(parsed) ? 0 : parseFloat(parsed.toFixed(2));
        }

        return 0;
    }

    /**
     * Check if pricing should be fetched based on profile configuration
     * @param {Object} profile - Profile configuration
     * @returns {boolean} True if prices should be fetched
     */
    shouldFetchPrices(profile) {
        return profile?.defaultValues?.withPrices === true;
    }

    /**
     * Get product prices for given product IDs with correct Shopware 6 pricing hierarchy
     * Priority: Sales Channel Rule → Customer Group Rule → Default "Shopkunden" Rule
     * @param {Array} productIds - Array of product IDs
     * @param {string} salesChannelId - Sales channel ID
     * @param {Object} customer - Customer object (optional)
     * @returns {Object} Map of product ID to price information
     */
    async getProductPrices(productIds, salesChannelId, customer = null) {
        if (!productIds || productIds.length === 0) return {};
        
        try {
            if (DEBUG_API) {
                console.log('[Price Calculator] 🔍 Fetching prices for products:', productIds, 'in sales channel:', salesChannelId, 'for customer:', customer?.customerNumber);
            }
            
            // Step 1: Get sales channel configuration and determine price rule hierarchy
            const salesChannelResponse = await this.api.get(`/sales-channel/${salesChannelId}`, {
                associations: {
                    customerGroup: {
                        associations: {
                            rule: {}
                        }
                    },
                    currency: {}
                }
            });
            
            const salesChannelData = salesChannelResponse.data?.data;
            const currencyId = salesChannelData?.currencyId;
            
            // Step 2: Determine pricing rule hierarchy based on requirements
            const pricingRules = await this.determinePricingRules(salesChannelId, salesChannelData, customer, productIds);
            
            if (DEBUG_API) {
                console.log('[Price Calculator] 📊 Determined pricing rules hierarchy:', pricingRules);
            }
            
            // Step 3: Fetch products with all pricing rules
            const productResponse = await this.api.post('/search/product', {
                filter: [
                    {
                        type: 'equalsAny',
                        field: 'id',
                        value: productIds
                    }
                ],
                associations: {
                    prices: pricingRules.allRuleIds.length > 0 ? {
                        // Filter prices to only include relevant rules for performance
                        filter: [
                            {
                                type: 'equalsAny',
                                field: 'ruleId',
                                value: pricingRules.allRuleIds
                            }
                        ]
                    } : {} // Fetch all prices if no specific rules found
                }
            });
            
            const products = productResponse.data?.data || [];
            const pricesMap = {};
            
            // Step 4: Process each product's pricing according to rule hierarchy
            for (const product of products) {
                const productId = product.id;
                const allPrices = product.prices || [];
                
                if (DEBUG_API) {
                    console.log(`[Price Calculator] 🛍️ Processing product ${product.productNumber}:`, allPrices.length, 'price rules available');
                    allPrices.forEach(price => {
                        console.log(`  - Rule ID: ${price.ruleId}, Quantity: ${price.quantityStart}-${price.quantityEnd || '∞'}`);
                    });
                }
                
                // Find the best matching price according to rule hierarchy
                const bestPrice = this.findBestPriceByRuleHierarchy(allPrices, {
                    currencyId,
                    pricingRules,
                    salesChannelId
                });
                
                if (bestPrice) {
                    pricesMap[productId] = bestPrice;
                    
                    // IMPROVEMENT: Enhanced logging for better price traceability
                    if (DEBUG_API) {
                        const priceTypeIndicator = bestPrice.source === 'system-fallback-shopkunden' ? '🔄 FALLBACK' : '✅';
                        console.log(`[Price Calculator] ${priceTypeIndicator} Selected ${bestPrice.source} price for ${product.productNumber}:`);
                        console.log(`  - Unit Price: ${bestPrice.unitPrice} NET (Rule: ${bestPrice.ruleId})`);
                        if (bestPrice.gross && bestPrice.net) {
                            console.log(`  - Gross: ${bestPrice.gross}, Net: ${bestPrice.net} (using NET for order)`);
                        }
                        if (bestPrice.coverage !== undefined) {
                            console.log(`  - Rule Coverage: ${Math.round(bestPrice.coverage * 100)}% (${bestPrice.productsWithPrices}/${bestPrice.totalProducts} products)`);
                        }
                        if (bestPrice.source === 'system-fallback-shopkunden') {
                            console.warn(`  ⚠️ FALLBACK PRICE USED - Check sales channel and customer group configuration`);
                        }
                    }
                } else {
                    if (DEBUG_API) {
                        console.log(`[Price Calculator] ⚠️ No suitable price found for product ${product.productNumber}`);
                    }
                }
            }
            
            // IMPROVEMENT: Enhanced final logging with fallback statistics
            if (DEBUG_API) {
                const fallbackCount = Object.values(pricesMap).filter(p => p.source === 'system-fallback-shopkunden').length;
                console.log('[Price Calculator] ⚡ Final pricing results:', Object.keys(pricesMap).length, 'products priced');
                if (fallbackCount > 0) {
                    console.warn(`[Price Calculator] 🔄 ${fallbackCount} products using FALLBACK prices - Review pricing configuration`);
                }
            }
            
            return pricesMap;
            
        } catch (error) {
            console.error('[Price Calculator] Error fetching product prices:', error);
            return {};
        }
    }

    /**
     * Find rules that apply to a specific sales channel ID
     * @param {string} salesChannelId - Sales channel ID to search for
     * @returns {Array} Array of matching rules
     */
    async findRulesBySalesChannelId(salesChannelId) {
        try {
            const response = await this.api.post('/search/rule', {
                filter: [
                    {
                        type: 'multi',
                        operator: 'AND',
                        queries: [
                            { type: 'equals', field: 'conditions.type', value: 'salesChannel' },
                            { type: 'contains', field: 'conditions.value', value: salesChannelId }
                        ]
                    }
                ],
                associations: {
                    conditions: {}
                }
            });
            
            return response.data?.data || [];
        } catch (error) {
            if (DEBUG_API) {
                console.warn('[Price Calculator] Error finding rules by sales channel ID:', error);
            }
            return [];
        }
    }

    /**
     * Validate if a rule has product prices for given product IDs
     * IMPROVED: Tests ALL products instead of just first 5 for complete validation
     * @param {string} ruleId - Rule ID to validate
     * @param {Array} productIds - Array of product IDs to check
     * @returns {Object} Validation result with coverage details
     */
    async validateRuleHasProductPrices(ruleId, productIds) {
        try {
            // IMPROVEMENT: Test ALL products instead of just first 5
            const testResponse = await this.api.post('/search/product', {
                filter: [
                    {
                        type: 'equalsAny',
                        field: 'id',
                        value: productIds // Test ALL products for complete validation
                    }
                ],
                associations: {
                    prices: {
                        filter: [
                            {
                                type: 'equals',
                                field: 'ruleId',
                                value: ruleId
                            }
                        ]
                    }
                },
                limit: Math.max(productIds.length, 25) // Ensure we get all products
            });
            
            const products = testResponse.data?.data || [];
            const productsWithPrices = products.filter(product => 
                product.prices && product.prices.length > 0
            );
            
            const coverage = products.length > 0 ? (productsWithPrices.length / products.length) : 0;
            const hasAnyPrices = productsWithPrices.length > 0;
            
            if (DEBUG_API) {
                console.log(`[Price Calculator] 🔍 Rule ${ruleId} validation:`, {
                    hasAnyPrices,
                    coverage: `${Math.round(coverage * 100)}%`,
                    productsWithPrices: productsWithPrices.length,
                    totalProducts: products.length,
                    missingPrices: products.length - productsWithPrices.length
                });
            }
            
            return {
                hasAnyPrices,
                coverage,
                productsWithPrices: productsWithPrices.length,
                totalProducts: products.length,
                missingPrices: products.length - productsWithPrices.length
            };
        } catch (error) {
            if (DEBUG_API) {
                console.warn('[Price Calculator] Error validating rule prices:', error);
            }
            return {
                hasAnyPrices: false,
                coverage: 0,
                productsWithPrices: 0,
                totalProducts: 0,
                missingPrices: 0,
                error: error.message
            };
        }
    }

    /**
     * Find rules that apply to a specific customer group ID
     * @param {string} customerGroupId - Customer group ID to search for
     * @returns {Array} Array of matching rules
     */
    async findRulesByCustomerGroupId(customerGroupId) {
        try {
            const response = await this.api.post('/search/rule', {
                filter: [
                    {
                        type: 'multi',
                        operator: 'AND',
                        queries: [
                            { type: 'equals', field: 'conditions.type', value: 'customerCustomerGroup' },
                            { type: 'contains', field: 'conditions.value', value: customerGroupId }
                        ]
                    }
                ],
                associations: {
                    conditions: {}
                }
            });
            
            return response.data?.data || [];
        } catch (error) {
            if (DEBUG_API) {
                console.warn('[Price Calculator] Error finding rules by customer group ID:', error);
            }
            return [];
        }
    }

    /**
     * Determine pricing rule hierarchy based on sales channel and customer
     * Priority: Sales Channel Rule → Customer Group Rule → Default "Shopkunden" Rule
     * @param {string} salesChannelId - Sales channel ID
     * @param {Object} salesChannelData - Sales channel configuration
     * @param {Object} customer - Customer object (optional)
     * @param {Array} productIds - Array of product IDs to validate rules against
     * @returns {Object} Pricing rules hierarchy
     */
    async determinePricingRules(salesChannelId, salesChannelData, customer = null, productIds = []) {
        const rules = {
            salesChannelRuleId: null,
            customerGroupRuleId: null,
            defaultShopkundenRuleId: null,
            allRuleIds: [],
            hierarchy: []
        };
        
        try {
            // STEP 1: Find ALL potentially applicable rules in parallel
            const [salesChannelRules, salesChannelCustomerGroupRules, customerGroupRules] = await Promise.all([
                // Find rules for sales channel ID
                this.findRulesBySalesChannelId(salesChannelId),
                // Find rules for sales channel's customer group
                salesChannelData?.customerGroupId ? this.findRulesByCustomerGroupId(salesChannelData.customerGroupId) : Promise.resolve([]),
                // Find rules for customer's group (if different from sales channel group)
                (async () => {
                    if (customer?.id) {
                        const customerResponse = await this.api.get(`/customer/${customer.id}`, {
                            associations: { group: {} }
                        });
                        const customerData = customerResponse.data?.data;
                        if (customerData?.group?.id && customerData.group.id !== salesChannelData?.customerGroupId) {
                            return this.findRulesByCustomerGroupId(customerData.group.id);
                        }
                    }
                    return [];
                })()
            ]);

            if (DEBUG_API) {
                console.log('[Price Calculator] 🔍 Found candidate rules:');
                console.log('  - Sales Channel Rules:', salesChannelRules.length);
                console.log('  - Sales Channel Customer Group Rules:', salesChannelCustomerGroupRules.length);
                console.log('  - Customer Group Rules:', customerGroupRules.length);
            }

            // STEP 2: Collect all unique candidate rules with priority
            const candidateRules = [];
            
            // Add sales channel rules (highest priority)
            salesChannelRules.forEach(rule => {
                candidateRules.push({
                    ...rule,
                    source: `sales-channel-${rule.name}`,
                    priority: 1
                });
            });
            
            // Add sales channel customer group rules 
            salesChannelCustomerGroupRules.forEach(rule => {
                if (!candidateRules.find(cr => cr.id === rule.id)) {
                    candidateRules.push({
                        ...rule,
                        source: `sales-channel-customer-group-${rule.name}`,
                        priority: 2
                    });
                }
            });
            
            // Add customer-specific group rules
            customerGroupRules.forEach(rule => {
                if (!candidateRules.find(cr => cr.id === rule.id)) {
                    candidateRules.push({
                        ...rule,
                        source: `customer-group-${rule.name}`,
                        priority: 3
                    });
                }
            });

            // STEP 3: Sort by priority and validate which rules actually have product prices
            if (candidateRules.length > 0) {
                candidateRules.sort((a, b) => a.priority - b.priority);
                
                if (DEBUG_API) {
                    console.log('[Price Calculator] 🧪 Testing candidate rules for product availability:');
                    candidateRules.forEach(rule => {
                        console.log(`  - ${rule.source}: ${rule.id} (priority: ${rule.priority})`);
                    });
                }
                
                // IMPROVEMENT: Test all candidate rules and select best coverage
                let bestRule = null;
                let bestValidation = null;
                
                for (const candidateRule of candidateRules) {
                    const validationResult = await this.validateRuleHasProductPrices(candidateRule.id, productIds);
                    
                    if (validationResult.hasAnyPrices) {
                        if (DEBUG_API) {
                            console.log('[Price Calculator] ✅ Rule has product prices:', candidateRule.id, 
                                'source:', candidateRule.source, 
                                'coverage:', `${Math.round(validationResult.coverage * 100)}%`);
                        }
                        
                        // IMPROVEMENT: Select rule with best coverage, or first high-priority rule with 100% coverage
                        if (!bestRule || 
                            validationResult.coverage > bestValidation.coverage ||
                            (validationResult.coverage === 1.0 && candidateRule.priority < bestRule.priority)) {
                            bestRule = candidateRule;
                            bestValidation = validationResult;
                        }
                        
                        // If we found a high-priority rule with 100% coverage, use it immediately
                        if (candidateRule.priority <= 2 && validationResult.coverage === 1.0) {
                            break;
                        }
                    } else if (DEBUG_API) {
                        console.log('[Price Calculator] ❌ Rule has no product prices:', candidateRule.id, 'source:', candidateRule.source);
                    }
                }
                
                // Add the best rule found
                if (bestRule && bestValidation) {
                    if (bestRule.priority <= 2) {
                        rules.salesChannelRuleId = bestRule.id;
                    } else {
                        rules.customerGroupRuleId = bestRule.id;
                    }
                    
                    rules.hierarchy.push({
                        ruleId: bestRule.id,
                        source: bestRule.source,
                        priority: bestRule.priority,
                        coverage: bestValidation.coverage,
                        productsWithPrices: bestValidation.productsWithPrices,
                        totalProducts: bestValidation.totalProducts
                    });
                    
                    if (DEBUG_API) {
                        console.log('[Price Calculator] 🎯 Selected BEST rule:', bestRule.id, 
                            'source:', bestRule.source, 
                            'coverage:', `${Math.round(bestValidation.coverage * 100)}%`,
                            `(${bestValidation.productsWithPrices}/${bestValidation.totalProducts} products)`);
                    }
                }
                
                if (rules.hierarchy.length === 0 && DEBUG_API) {
                    console.log('[Price Calculator] ⚠️ No candidate rules have product prices, will use fallback logic');
                }
            }
            
            // PRIORITY 4: Add fixed system fallback rule for pricing
            const SYSTEM_FALLBACK_RULE_ID = '018ad19c51e572e4a6ad9fab218a04b8';
            if (rules.hierarchy.length === 0 || !rules.hierarchy.find(r => r.ruleId === SYSTEM_FALLBACK_RULE_ID)) {
                rules.defaultShopkundenRuleId = SYSTEM_FALLBACK_RULE_ID;
                rules.hierarchy.push({
                    ruleId: SYSTEM_FALLBACK_RULE_ID,
                    source: 'system-fallback-shopkunden',
                    priority: 99
                });
                
                if (DEBUG_API) {
                    console.log('[Price Calculator] 🔧 Added system fallback rule:', SYSTEM_FALLBACK_RULE_ID, '(Shopkunden)');
                }
            }
            
            // Collect all rule IDs for filtering
            rules.allRuleIds = rules.hierarchy.map(rule => rule.ruleId).filter(Boolean);
            
            if (DEBUG_API) {
                console.log('[Price Calculator] 📋 Pricing rules determined:', {
                    salesChannelRuleId: rules.salesChannelRuleId,
                    customerGroupRuleId: rules.customerGroupRuleId,
                    defaultShopkundenRuleId: rules.defaultShopkundenRuleId,
                    hierarchyCount: rules.hierarchy.length
                });
            }
            
        } catch (error) {
            console.warn('[Price Calculator] Error determining pricing rules:', error);
        }
        
        return rules;
    }
    
    /**
     * Find the best price for a product according to rule hierarchy
     * Priority: Sales Channel Rule → Customer Group Rule → Default "Shopkunden" Rule
     * @param {Array} prices - Array of price objects from product.prices
     * @param {Object} context - Pricing context with rule hierarchy and currency
     * @returns {Object|null} Best price object or null if no price found
     */
    findBestPriceByRuleHierarchy(prices, context) {
        const { currencyId, pricingRules } = context;
        
        if (!prices || prices.length === 0) return null;
        
        // Process rules in priority order (if any rules exist)
        if (pricingRules.hierarchy && pricingRules.hierarchy.length > 0) {
            for (const rule of pricingRules.hierarchy) {
                const matchingPrice = prices.find(p => p.ruleId === rule.ruleId);
                if (matchingPrice) {
                    const extractedPrice = this.extractPriceFromPriceObject(matchingPrice, currencyId);
                    if (extractedPrice) {
                        return {
                            ...extractedPrice,
                            ruleId: matchingPrice.ruleId,
                            source: rule.source,
                            priority: rule.priority,
                            quantityStart: matchingPrice.quantityStart,
                            quantityEnd: matchingPrice.quantityEnd
                        };
                    }
                }
            }
        }
        
        // Last resort: any available pricing
        if (prices.length > 0) {
            const sortedPrices = [...prices].sort((a, b) => (a.quantityStart || 1) - (b.quantityStart || 1));
            
            for (const price of sortedPrices) {
                const extractedPrice = this.extractPriceFromPriceObject(price, currencyId);
                if (extractedPrice) {
                    return {
                        ...extractedPrice,
                        ruleId: price.ruleId,
                        source: 'fallback',
                        priority: 99,
                        quantityStart: price.quantityStart,
                        quantityEnd: price.quantityEnd
                    };
                }
            }
        }
        
        return null;
    }

    /**
     * Extract price information from a Shopware 6 price object
     * @param {Object} priceObject - Price object from product.prices array
     * @param {string} currencyId - Currency ID to extract
     * @returns {Object|null} Extracted price information or null
     */
    extractPriceFromPriceObject(priceObject, currencyId) {
        if (!priceObject || !priceObject.price) return null;
        
        // Find the price for the specific currency
        const currencyPrice = priceObject.price.find(p => p.currencyId === currencyId);
        if (!currencyPrice) return null;
        
        // Extract unit price (prefer net over gross for order import)
        const unitPrice = currencyPrice.net || currencyPrice.gross || 0;
        
        return {
            unitPrice: parseFloat(unitPrice.toFixed(2)),
            gross: currencyPrice.gross ? parseFloat(currencyPrice.gross.toFixed(2)) : currencyPrice.gross,
            net: currencyPrice.net ? parseFloat(currencyPrice.net.toFixed(2)) : currencyPrice.net,
            linked: currencyPrice.linked
        };
    }

    /**
     * Advanced price calculation for profile-based imports
     * IMPROVED: Added configurable strict product validation
     * @param {Array} productNumbers - Array of product numbers to price
     * @param {Object} profile - Profile configuration with validation settings
     * @param {Object} customer - Customer object (optional)
     * @returns {Object} Map of product ID to price information or error details
     */
    async getProductPricesForProfile(productNumbers, profile, customer = null) {
        if (!productNumbers || productNumbers.length === 0) return {};
        
        try {
            if (DEBUG_API) {
                console.log('[Price Calculator] ⚡ ENTERING getProductPricesForProfile');
                console.log('[Price Calculator] Products to price:', productNumbers.length);
                console.log('[Price Calculator] Profile withPrices:', profile?.defaultValues?.withPrices);
                console.log('[Price Calculator] Strict validation:', profile?.defaultValues?.strictProductValidation);
                console.log('[Price Calculator] Customer:', customer?.customerNumber);
            }
            
            // Early return if pricing is disabled
            if (!this.shouldFetchPrices(profile)) {
                if (DEBUG_API) {
                    console.log('[Price Calculator] ⚡ EXITING getProductPricesForProfile - pricing disabled');
                }
                return {};
            }
            
            // Get sales channel from profile
            const salesChannelId = profile.defaultValues?.salesChannelId;
            if (!salesChannelId) {
                console.warn('[Price Calculator] No sales channel configured in profile');
                return {};
            }
            
            // Find products by numbers first
            const productSearchResponse = await this.api.post('/search/product', {
                filter: [
                    {
                        type: 'equalsAny',
                        field: 'productNumber',
                        value: productNumbers
                    }
                ],
                associations: {
                    prices: {}
                }
            });
            
            const products = productSearchResponse.data?.data || [];
            const productIds = products.map(p => p.id);
            
            // IMPROVEMENT: Product validation logic
            const foundProductNumbers = products.map(p => p.productNumber);
            const missingProducts = productNumbers.filter(pn => !foundProductNumbers.includes(pn));
            
            if (DEBUG_API) {
                console.log('[Price Calculator] Found products:', products.length, 'of', productNumbers.length, 'requested');
                if (missingProducts.length > 0) {
                    console.log('[Price Calculator] ⚠️ Missing products:', missingProducts);
                }
            }
            
            // IMPROVEMENT: Strict product validation (configurable)
            if (profile.defaultValues?.strictProductValidation && missingProducts.length > 0) {
                const errorMessage = `Missing products in Shopware: ${missingProducts.join(', ')}`;
                console.error('[Price Calculator] 🚫 Strict validation failed:', errorMessage);
                
                return {
                    success: false,
                    error: 'MISSING_PRODUCTS',
                    message: errorMessage,
                    missingProducts: missingProducts,
                    foundProducts: foundProductNumbers,
                    totalRequested: productNumbers.length,
                    totalFound: products.length
                };
            }
            
            // Get prices for found products
            const pricesMap = await this.getProductPrices(productIds, salesChannelId, customer);
            
            // Map back to product numbers for easier lookup
            const productNumberPricesMap = {};
            for (const product of products) {
                const priceInfo = pricesMap[product.id];
                if (priceInfo) {
                    productNumberPricesMap[product.productNumber] = {
                        ...priceInfo,
                        productId: product.id
                    };
                }
            }
            
            if (DEBUG_API) {
                console.log('[Price Calculator] ⚡ EXITING getProductPricesForProfile - Found prices for', Object.keys(productNumberPricesMap).length, 'products');
            }
            
            // IMPROVEMENT: Return validation results even on success
            const result = {
                success: true,
                prices: productNumberPricesMap,
                validation: {
                    totalRequested: productNumbers.length,
                    totalFound: products.length,
                    missingProducts: missingProducts,
                    strictValidation: profile.defaultValues?.strictProductValidation || false
                }
            };
            
            // Add warnings for partial success
            if (missingProducts.length > 0) {
                result.warnings = [`${missingProducts.length} products not found: ${missingProducts.join(', ')}`];
            }
            
            return result;
            
        } catch (error) {
            console.error('[Price Calculator] Error in advanced price lookup:', error);
            return {
                success: false,
                error: 'API_ERROR',
                message: error.message,
                prices: {}
            };
        }
    }

    /**
     * Calculate tax for given amount and tax rate
     * @param {number} amount - Amount to calculate tax for
     * @param {number} taxRate - Tax rate percentage
     * @param {boolean} isTaxFree - Whether the calculation is tax-free
     * @returns {Object} Tax calculation result
     */
    calculateTax(amount, taxRate, isTaxFree) {
        const taxPrice = taxRate / 100;
        const taxAmount = isTaxFree ? 0 : amount * taxPrice;
        const totalAmount = amount + taxAmount;
        
        return {
            netAmount: parseFloat(amount.toFixed(2)),
            taxAmount: parseFloat(taxAmount.toFixed(2)),
            totalAmount: parseFloat(totalAmount.toFixed(2)),
            taxRate: taxRate,
            isTaxFree: isTaxFree
        };
    }

    /**
     * Calculate line item pricing
     * @param {Object} params - Calculation parameters
     * @returns {Object} Line item price structure
     */
    calculateLineItemPrice(params) {
        const {
            unitPrice = 0,
            quantity = 1,
            taxRate = 19,
            isTaxFree = false,
            withPrices = false
        } = params;
        
        const finalUnitPrice = withPrices ? unitPrice : 0;
        const totalPrice = finalUnitPrice * quantity;
        const taxCalculation = this.calculateTax(totalPrice, taxRate, isTaxFree);
        
        return {
            unitPrice: parseFloat(finalUnitPrice.toFixed(2)),
            totalPrice: parseFloat(totalPrice.toFixed(2)),
            quantity: quantity,
            calculatedTaxes: isTaxFree ? [] : [{
                extensions: [],
                tax: parseFloat(taxCalculation.taxAmount.toFixed(2)),
                taxRate: taxRate,
                price: parseFloat(totalPrice.toFixed(2))
            }],
            taxRules: isTaxFree ? [] : [{
                extensions: [],
                taxRate: taxRate,
                percentage: 100.0
            }],
            referencePrice: null,
            listPrice: null,
            regulationPrice: null
        };
    }

    /**
     * Calculate order total pricing
     * @param {number} totalPrice - Total order amount
     * @param {number} taxRate - Tax rate percentage
     * @param {boolean} isTaxFree - Whether order is tax-free
     * @returns {Object} Order price structure
     */
    calculateOrderPrice(totalPrice, taxRate, isTaxFree) {
        // Fix: Don't use .toFixed() here to avoid NaN issues
        const taxCalculation = this.calculateTax(totalPrice, taxRate, isTaxFree);
        
        return {
            netPrice: totalPrice.toFixed(2),
            totalPrice: taxCalculation.totalAmount.toFixed(2),
            calculatedTaxes: isTaxFree ? [] : [{
                extensions: [],
                tax: taxCalculation.taxAmount.toFixed(2),
                taxRate: taxRate,
                price: totalPrice.toFixed(2)
            }],
            taxRules: isTaxFree ? [] : [{
                extensions: [],
                taxRate: taxRate,
                percentage: 100.0
            }],
            positionPrice: totalPrice.toFixed(2),
            rawTotal: taxCalculation.totalAmount.toFixed(2),
            taxStatus: isTaxFree ? "tax-free" : "net"
        };
    }
}

export default PriceCalculator;